import {
  IfpCompanyForm,
  IfpEmploymentType,
  IfpOccupation,
  IfpWorkingHours,
  PrimaryReasonForIfp,
  SecLoanCarOwnershipType,
  SecLoanChildrenAgeGroup,
  SecLoanOtherIncomeType,
  TimeUntilEmployedAgainEstimation,
} from '@shared/sec-loans'
import { LocalizedString } from '@shared/types'

// type for FE & BFF API
export interface IfpLoan {
  uuid: string
  isAvailable: boolean
  iban: string
  balance: number
  description: LocalizedString
  principalExemptionMonthsLeft?: number
  debtors: {
    uuid: string
    name: string
  }[]
}

export enum IfpApplicationState {
  UnavailableOther = 'UnavailableOther',
  UnavailableDraftInvalidated = 'UnavailableDraftInvalidated',
  Draft = 'Draft',
  WaitingForCoDebtor = 'WaitingForCoDebtor',
  ReturnedByCoDebtor = 'ReturnedByCoDebtor',
  Cancelled = 'Cancelled',
  Sending = 'Sending',
  WaitingForResend = 'WaitingForResend',
  Rejected = 'Rejected',
  Pending = 'Pending',
  Accepted = 'Accepted',
}

export enum IfpUserState {
  Returned = 'Returned',
  Cancelled = 'Cancelled',
  Accepted = 'Accepted',
}

export interface IfpApplicationMetadata extends Pick<
  IfpApplication,
  'applicationId' | 'updated' | 'state' | 'stateChangedBy' | 'actionsNeeded' | 'primaryUuid'
> {
  loan: Pick<IfpLoan, 'balance' | 'description' | 'uuid'>
}

export type IfpApplicationRequestApplicantInfo = IfpApplicationApplicantInfo & { uuid: string }

export interface IfpApplicationRequestData {
  basicInfo: IfpApplicationBasicInfo
  householdInfo?: IfpApplicationHouseholdInfo
  applicantInfos: IfpApplicationRequestApplicantInfo[]
}

export interface IfpApplicationBasicInfo {
  durationInMonths: number
  startsImmediately: boolean
  startDate?: string
  primaryReason: PrimaryReasonForIfp
  primaryReasonOther?: string
  hasFinancialDifficulties: boolean
}

export interface IfpApplicationHouseholdInfo {
  numberOfAdults: number
  numberOfChildren: number
}

export interface IfpApplicationApplicantInfo {
  phoneNumber: string
  occupation?: IfpOccupation
  grossSalary?: number
  netSalary?: number
  employmentType?: IfpEmploymentType
  employer?: string
  workingHours?: IfpWorkingHours
  profession?: string
  employmentStartDate?: string
  employmentEndDate?: string
  estimatedFurloughEndDate?: string
  currentIncomeBeforeTaxes?: number
  currentIncomeAfterTaxes?: number
  normalGrossSalary?: number
  normalNetSalary?: number
  companyName?: string
  businessId?: string
  incomeBeforeTaxes?: number
  incomeAfterTaxes?: number
  legalFormOfTheCompany?: IfpCompanyForm
  companyFoundingDate?: string
  estimatedReturnToWorkDate?: string
  hasJobToReturnTo?: boolean
  expectsToBeEmployedAgain?: boolean
  timeUntilEmployedAgainEstimation?: TimeUntilEmployedAgainEstimation
  otherIncomes?: {
    incomeType: SecLoanOtherIncomeType
    amountAfterTaxes: number
  }[]
  totalSavings?: number
  ownShareOfHousingExpenses?: number
  carOwnershipType?: SecLoanCarOwnershipType
  childSupports?: {
    ageGroup: SecLoanChildrenAgeGroup
    supportAmount: number
  }[]
  totalLoansAmount?: number
  totalAmountOfLoanRepaymentsPerMonth?: number
  totalHousingCompanyLoan?: number
}

export interface IfpApplication<TData = IfpApplicationRequestData> {
  applicationId: string
  created: number
  updated: number
  version: string
  actionsNeeded: string[] | null
  primaryUuid: string
  state: IfpApplicationState
  stateChangedBy: string
  data?: Partial<TData>
}

export interface IfpApplicationIdPathParam {
  applicationId: string
}

export enum IfpApplicationAction {
  Return = 'return',
  Cancel = 'cancel',
}

export type IfpApplicationSendActionPathParams = IfpApplicationIdPathParam

export interface IfpApplicationReturnOrCancelActionPathParams extends IfpApplicationIdPathParam {
  action: IfpApplicationAction
}

export type IfpGetApplicationResponse = IfpApplication

/** ApplicationId */
export type IfpCreateApplicationResponse = string

export type IfpApplicationActionResponse = {
  applicationState: IfpApplicationState | undefined
}

export interface IfpCreateApplicationRequest {
  loanUuid: IfpLoan['uuid']
  data?: Partial<IfpApplicationRequestData>
}

export interface IfpUpdateApplicationRequest {
  data: Partial<IfpApplicationRequestData>
}

export interface IfpApplicationSendActionRequest {
  authCode: string
}
